import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import dynamic from 'next/dynamic'
import { usePlausible } from 'next-plausible'
import { useRouter } from 'next/router'
import { Inter } from 'next/font/google'
import { Transition, TransitionChild } from '@headlessui/react'
import Logo from '@/components/nav/logo'
import UpsShield from '@/components/ui/ups-shield'
import BookButton from '@/components/ui/BookButton'
import MobileSwitcher from '@/components/navbar/MobileSwitcher'
import EssenceIcon from '@/components/navbar/essence-icon'
import { navigationMenuTriggerStyle } from '@/components/ui/navigation-menu-style'
import { usePageTheme } from '@/components/page-theme-provider'
import navigation from '@/constants/navigation'

const inter = Inter({ subsets: ['latin'], display: 'swap' })
const MobileMenu = dynamic(() => import('@/components/navbar/MobileMenu'), { ssr: false })
const services = navigation[0].links

const links = [
  ['Our Work', '/our-work'],
  ['About Us', '/about'],
  ['Case Studies', '/case-studies'],
  ['Resources', '/resources'],
]

export default function Nav({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false)
  const scrollAnchor = useRef(0)
  const revealAnimation = useRef(null)
  const [scrollTransitions, setScrollTransitions] = useState(false)
  const [open, setOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [mobileMenuRequested, setMobileMenuRequested] = useState(false)
  const pageTheme = usePageTheme()
  const root = useRef(null)
  const opener = useRef(null)
  const router = useRouter()
  const plausible = usePlausible()
  const solid = !transparent || scrolled
  const currentPath = router.asPath.split(/[?#]/)[0]
  const serviceActive = services.some(link => currentPath === `/${link.href}`)
  const isUpsPage = router.pathname === '/ups' || router.pathname.startsWith('/ups/')
  const bookingUrl = isUpsPage || router.pathname === '/new-ups'
    ? 'https://calendly.com/d/dtf-kzx-j3m'
    : '/contact-us'
  const orangeBranding = ['/', '/ups'].includes(router.pathname)
  const trackBooking = () => {
    if (router.pathname !== '/web-design') return
    if (typeof window.fbq === 'function') window.fbq('track', 'WebDesignNavbarContactUsClick')
    plausible('WebDesignNavbarContactUsClick')
  }

  const reveal = () => {
    revealAnimation.current?.cancel()
    if (root.current) root.current.dataset.pinned = 'true'
  }
  const close = () => setOpen(false)
  const openMenu = event => { reveal(); opener.current = event.currentTarget; setOpen(true) }
  const openMobileMenu = () => {
    document.documentElement.style.setProperty('--page-scrollbar-width', `${window.innerWidth - document.documentElement.clientWidth}px`)
    setMobileMenuRequested(true)
    setMobileOpen(true)
  }


  useEffect(() => {
    const getScrollTop = () => Math.max(0, Math.min(window.scrollY, document.documentElement.scrollHeight - window.innerHeight))
    let previousScrollTop = getScrollTop()
    let direction = 0
    let directionStart = previousScrollTop
    const update = () => {
      const scrollTop = getScrollTop()
      const header = root.current
      if (!header) return
      const height = header.getBoundingClientRect().height
      const delta = scrollTop - previousScrollTop
      const nextDirection = Math.sign(delta)
      if (nextDirection && nextDirection !== direction) {
        directionStart = previousScrollTop
        direction = nextDirection
      }
      if (header.dataset.open === 'true' || scrollTop === 0) {
        reveal()
      } else if (delta > 0) {
        if (header.dataset.pinned === 'true') {
          const visibleTop = header.getBoundingClientRect().top
          revealAnimation.current?.cancel()
          scrollAnchor.current = previousScrollTop + visibleTop
          header.dataset.pinned = 'false'
        }
        scrollAnchor.current = Math.max(scrollTop - height - 2, scrollAnchor.current)
        header.style.setProperty('--nav-anchor', `${scrollAnchor.current}px`)
      } else if (delta < 0 && directionStart - scrollTop < 4 && header.dataset.pinned !== 'true' && previousScrollTop - scrollAnchor.current >= height) {
        // Keep the hidden header offscreen while waiting for the reveal threshold.
        scrollAnchor.current = scrollTop - height - 2
        header.style.setProperty('--nav-anchor', `${scrollAnchor.current}px`)
      } else if (delta < 0 && directionStart - scrollTop >= 4 && header.dataset.pinned !== 'true') {
        const visibleTop = Math.min(0, header.getBoundingClientRect().top)
        header.dataset.pinned = 'true'
        if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          revealAnimation.current = header.animate(
            { transform: [`translateY(${visibleTop}px)`, 'translateY(0)'] },
            { duration: 350, easing: 'cubic-bezier(0.165, 0.84, 0.44, 1)' }
          )
        }
      }
      previousScrollTop = scrollTop
      setScrolled(scrollTop >= height)
    }
    let frame
    update()
    // Let the destination route and its restored scroll position paint first.
    frame = requestAnimationFrame(() => {
      update()
      frame = requestAnimationFrame(() => setScrollTransitions(true))
    })
    window.addEventListener('scroll', update, { passive: true })
    window.addEventListener('resize', update)
    return () => {
      cancelAnimationFrame(frame)
      revealAnimation.current?.cancel()
      window.removeEventListener('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [transparent, router.asPath])

  useEffect(() => {
    const dismiss = event => {
      if (!opener.current?.parentElement?.contains(event.target)) setOpen(false)
    }
    const keyboard = event => {
      if (event.key === 'Escape') {
        setOpen(false)
        setMobileOpen(false)
        opener.current?.focus()
      }
    }
    if (open) {
      document.addEventListener('pointerdown', dismiss)
      document.addEventListener('keydown', keyboard)
    }
    return () => {
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', keyboard)
    }
  }, [open])

  useEffect(() => {
    const dismiss = () => {
      // Disable transitions immediately, before route-driven scroll resets.
      root.current?.setAttribute('data-scroll-transitions', 'false')
      setScrollTransitions(false)
      setOpen(false)
      setMobileOpen(false)
      reveal()
    }
    router.events.on('routeChangeStart', dismiss)
    return () => router.events.off('routeChangeStart', dismiss)
  }, [router.events])

  return (
    <header ref={root} className="group/nav absolute inset-x-0 top-[var(--nav-anchor,0px)] data-[pinned=true]:fixed data-[pinned=true]:top-0 z-[150] border-b-[1.25px] border-transparent bg-transparent text-white [transition:background-color_220ms,color_220ms,border-color_220ms] data-[scrolled=true]:border-b-[#152a35]/5 data-[scrolled=true]:bg-white data-[scrolled=true]:text-black motion-reduce:transition-none data-[scroll-transitions=false]:!transition-none [&_a:focus-visible]:rounded-md [&_a:focus-visible]:outline [&_a:focus-visible]:outline-2 [&_a:focus-visible]:outline-current [&_a:focus-visible]:outline-offset-[5px] [&_button:focus-visible]:rounded-md [&_button:focus-visible]:outline [&_button:focus-visible]:outline-2 [&_button:focus-visible]:outline-current [&_button:focus-visible]:outline-offset-[5px]" data-scrolled={solid} data-scroll-transitions={scrollTransitions} data-open={open || mobileOpen} data-pinned="true"
      onFocusCapture={reveal}
      onClickCapture={event => { if (!mobileOpen && event.target.closest('[aria-controls="site-mobile-navigation"]')) opener.current = event.target.closest('button') }}
      onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget)) close() }}>
      <div className="mx-auto flex h-[81px] w-full max-w-screen-xl items-center justify-between px-5 md:px-10">
        <Link href="/" prefetch={false} className="relative z-50 block w-[200px] shrink-0 select-none" aria-label="Ventura home">
          <Logo variant={orangeBranding ? 'orange' : undefined} light={!solid} className={`block h-auto w-full ${orangeBranding ? '' : 'group-data-[scrolled=false]/nav:brightness-0 group-data-[scrolled=false]/nav:invert'}`} />
        </Link>
        <nav aria-label="Primary navigation" className="ml-auto mr-2.5 hidden select-none items-center justify-end gap-1 xl:flex">
          <div className="relative" onPointerLeave={event => { if (event.pointerType === 'mouse') close() }}>
            <button type="button" className={`${navigationMenuTriggerStyle()} ${open || serviceActive ? "bg-accent/50" : ""}`} data-state={open ? "open" : "closed"} aria-expanded={open} aria-controls="site-services"
              onClick={event => open ? close() : openMenu(event)}
              onPointerEnter={event => { if (event.pointerType === 'mouse') openMenu(event) }}
              onKeyDown={event => { if (event.key === 'ArrowDown') { event.preventDefault(); openMenu(event); requestAnimationFrame(() => root.current?.querySelector('#site-services a')?.focus()) } }}>
              Services <EssenceIcon name="chevron" className={`ml-3 size-3 transition-transform duration-200 motion-reduce:transition-none ${open ? "-rotate-90" : "rotate-90"}`} />
            </button>
            <Transition show={open} as="div" id="site-services" className="absolute -left-1 top-full hidden w-max pt-4 xl:block"
              aria-hidden={!open} inert={open ? undefined : ''}>
              <TransitionChild as="nav" aria-label="Services"
                enter="transition-transform duration-150 [transition-timing-function:ease] motion-reduce:transition-none" enterFrom="scale-90" enterTo="scale-100"
                leave="transition-transform duration-150 [transition-timing-function:ease] motion-reduce:transition-none" leaveFrom="scale-100" leaveTo="scale-95"
                className={`origin-top max-h-[calc(100svh-97px)] min-w-56 max-w-[calc(100vw-40px)] overflow-y-auto overscroll-contain rounded-2xl bg-white p-1 text-base leading-[1.5] text-black shadow-[0_8px_16px_#00000014] outline outline-1 -outline-offset-1 outline-black/10 ${inter.className}`}>
                <ul className="grid w-[480px] max-w-[calc(100vw-40px)] grid-cols-2 gap-1 p-1">
                  {services.map(link => (
                    <li key={link.href}>
                      <Link href={`/${link.href}`} onClick={close}
                        aria-current={currentPath === `/${link.href}` ? 'page' : undefined}
                        className={`block rounded-lg p-4 text-sm font-medium leading-snug hover:bg-accent/50 focus:bg-accent/50 ${currentPath === `/${link.href}` ? 'bg-accent/50' : ''}`}>
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </TransitionChild>
            </Transition>
          </div>
          {links.map(([label, href]) => <Link key={href} href={href} prefetch={false} className={`${navigationMenuTriggerStyle()} ${router.pathname !== "/" && router.pathname.includes(href) ? "!bg-accent/50" : ""}`}>{label}</Link>)}
          <Link href="/ups" prefetch={false} className={`${navigationMenuTriggerStyle()} !h-12 !w-12 !p-0 ${isUpsPage ? "!bg-accent/50" : ""}`} aria-label="UPS"><UpsShield className="h-[27px] w-[27px]" /></Link>
        </nav>
        <div className="hidden w-[155px] overflow-visible transition-[width] duration-200 xl:block">
          <BookButton color={orangeBranding ? 'orange' : pageTheme.key} text="Book a call" link={bookingUrl} onClick={trackBooking} custom="!w-[155px]" />
        </div>
        <MobileSwitcher mobileOpen={mobileOpen} onOpen={openMobileMenu} onIntent={() => setMobileMenuRequested(true)} />
      </div>
      {mobileMenuRequested && <MobileMenu mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} orangeBranding={orangeBranding} bookingUrl={bookingUrl} />}
    </header>
  )
}
